/**
 * Vertrag der Galerie- und Regelansichten (Stufe D): Liste, Detail, Match-Vorschau, Anlegen,
 * Einstellungen und normale Regeln speichern (Versionsvergleich über `expectedVersion`).
 * Interne Felder bleiben draußen; Regeln erscheinen als lesbare Zusammenfassung und als AST.
 */
import { z } from 'zod';
import { EbaySiteId, GallerySettings, IsoDateTime, Version } from '@pat/core/entities';
import { NormalRule } from '@pat/core/rules';
import { ReviseState } from './content.js';
import { ItemView } from './contract.js';
import { MAX_RULES } from './rule-input.js';

/** Wie die Galerie ihre Angebote wählt; abgeleitet aus dem RuleSet. */
export const LinkMode = z.enum(['all', 'same-store-category', 'same-ebay-category', 'custom']);
export type LinkMode = z.infer<typeof LinkMode>;

export const GallerySummaryView = z.object({
  galleryId: z.string(),
  /** Öffentliche Kennung `g` aus dem Einbettcode. */
  legacyG: z.string(),
  name: z.string(),
  site: EbaySiteId,
  linkMode: LinkMode,
  ruleCount: z.number().int().nonnegative(),
  scriptCount: z.number().int().nonnegative(),
  /** Regeln zur Prüfung gesperrt (nicht übersetzbare Altregeln). */
  locked: z.boolean(),
  updatedAt: IsoDateTime,
});
export type GallerySummaryView = z.infer<typeof GallerySummaryView>;

export const GalleriesResponse = z.object({
  galleries: z.array(GallerySummaryView),
  /** Rolle und Umzugszustand erlauben Anlegen und Bearbeiten. */
  editable: z.boolean(),
});
export type GalleriesResponse = z.infer<typeof GalleriesResponse>;

export const RuleView = z.object({
  ruleId: z.string(),
  kind: z.string(),
  priority: z.number().int(),
  /** Lesbare Kurzform, z. B. Kategorien oder Suchbegriffe. */
  summary: z.string(),
});
export type RuleView = z.infer<typeof RuleView>;

export const ScriptView = z.object({
  scriptId: z.string(),
  name: z.string().optional(),
  lineCount: z.number().int().nonnegative(),
  unsupportedLineCount: z.number().int().nonnegative(),
  /** Bis zu 50 Zeilen als Kurzform (Richtung, linke und rechte Seite). */
  lines: z.array(z.object({ lineNo: z.number().int(), text: z.string() })),
});
export type ScriptView = z.infer<typeof ScriptView>;

export const GalleryDetailResponse = z.object({
  gallery: GallerySummaryView.extend({ settings: GallerySettings, version: Version }),
  rules: z.object({
    version: Version,
    locked: z.object({ reason: z.string(), since: IsoDateTime }).optional(),
    scripts: z.array(ScriptView),
    rules: z.array(RuleView),
    /** Normale Regeln als AST für den Editor (Skripte bleiben lesend). */
    ast: z.array(NormalRule),
    /** Regelvergleich (`word` = Wortgrenze, `legacy-substring` = Teilstring wie im Altsystem). */
    sourceMatch: z.enum(['word', 'legacy-substring']),
    /** Rolle und Umzugszustand erlauben Bearbeiten. */
    editable: z.boolean(),
  }),
});
export type GalleryDetailResponse = z.infer<typeof GalleryDetailResponse>;

/**
 * Einstellungen aus dem Editor: core-Form plus die Regel der Engine, dass Zufall nicht mit
 * einer Sortierung 1..3 zusammengeht (sonst lehnt sie mit `unsupported-sort` ab).
 */
export const GallerySettingsInput = GallerySettings.superRefine((settings, ctx) => {
  if (settings.randomize && settings.sortMode >= 1 && settings.sortMode <= 3) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['randomize'], message: 'Zufällige Reihenfolge geht nicht zusammen mit einer Sortierung' });
  }
});

const GalleryName = z.string().trim().min(1).max(120);

/** Kennung neuer Galerien: die SPA erzeugt sie einmal je Anlegevorgang, damit eine Wiederholung idempotent ist. */
export const NEW_GALLERY_ID = /^gal-[0-9a-f]{12,32}$/;
export const GalleryCreateRequest = z
  .object({ galleryId: z.string().regex(NEW_GALLERY_ID).optional(), name: GalleryName, site: EbaySiteId, settings: GallerySettingsInput.optional() })
  .strict();
export type GalleryCreateRequest = z.infer<typeof GalleryCreateRequest>;
export const GalleryCreateResponse = z.object({ galleryId: z.string(), legacyG: z.string() });
export type GalleryCreateResponse = z.infer<typeof GalleryCreateResponse>;

export const GallerySaveRequest = z.object({ expectedVersion: Version, name: GalleryName, settings: GallerySettingsInput }).strict();
export type GallerySaveRequest = z.infer<typeof GallerySaveRequest>;

/**
 * Was das Löschen berührt (Flori 25.10.2026, Entscheidung 10): Designs mit einer Galeriebox dieser Galerie und deren
 * Artikel, die Einfügeposition und ob der Worker den Block jetzt entfernen würde (`removal`, wie `ReviseState`).
 * `embeds`: Angebote, in die PAT die Galerie zuletzt geschrieben hat (`EMBED#.galleries`); fehlt, wenn die Zählung
 * nicht möglich war. `unresolved` = nie von PAT geschriebene Beschreibungen (Altsystem), deren Inhalt unbekannt ist.
 */
export const GalleryEmbedCountView = z.object({
  complete: z.boolean(),
  count: z.number().int().nonnegative(),
  unresolved: z.number().int().nonnegative(),
});
export type GalleryEmbedCountView = z.infer<typeof GalleryEmbedCountView>;
export const GalleryUsageResponse = z.object({
  designs: z.number().int().nonnegative(),
  articles: z.number().int().nonnegative(),
  insertMode: GallerySettings.shape.insertMode,
  removal: ReviseState,
  embeds: GalleryEmbedCountView.optional(),
});
export type GalleryUsageResponse = z.infer<typeof GalleryUsageResponse>;
/** Angebote mit dieser Galerie (aufsteigend nach Angebotsnummer, ohne Index nach Galerie seitenweise gelesen). */
export const GALLERY_ITEMS_PAGE_DEFAULT = 25;
export const GALLERY_ITEMS_PAGE_MAX = 50;
export const GalleryItemsQuery = z.object({
  limit: z.coerce.number().int().min(1).max(GALLERY_ITEMS_PAGE_MAX).default(GALLERY_ITEMS_PAGE_DEFAULT),
  cursor: z.string().min(1).max(2048).optional(),
}).strict();
/** `item` fehlt, wenn das Angebot (noch) nicht im Bestand ist; Zustand und Modus kommen aus `EMBED#`. */
export const GalleryItemEntry = z.object({ ebayItemId: z.string(), mode: z.enum(['full', 'block']).optional(), item: ItemView.optional() });
export type GalleryItemEntry = z.infer<typeof GalleryItemEntry>;
export const GalleryItemsResponse = z.object({ entries: z.array(GalleryItemEntry).max(GALLERY_ITEMS_PAGE_MAX), nextCursor: z.string().optional() });
export type GalleryItemsResponse = z.infer<typeof GalleryItemsResponse>;
/** `DELETE …/galleries/:id?expectedVersion=<n>`: weich, nicht rückgängig zu machen; gelöschte Galerien erscheinen nicht mehr. */
export const GalleryDeleteQuery = z.object({ expectedVersion: z.string().regex(/^[0-9]{1,9}$/).transform(Number) }).strict();
export const GalleryDeleteResponse = z.object({ galleryId: z.string(), deleted: z.literal(true) });
export type GalleryDeleteResponse = z.infer<typeof GalleryDeleteResponse>;

/** Normale Regeln als Gesamtliste; `removeScripts` ersetzt Altskripte durch diese Regeln (hebt deren Sperre auf). */
export const RulesDraft = z.object({ rules: z.array(NormalRule).max(MAX_RULES), removeScripts: z.boolean().optional() }).strict();
export type RulesDraft = z.infer<typeof RulesDraft>;
export const RulesSaveRequest = RulesDraft.extend({ expectedVersion: Version }).strict();
export type RulesSaveRequest = z.infer<typeof RulesSaveRequest>;

export const MatchRequest = z
  .object({
    /** Ausgangsangebot; ohne nur Layout-/Fallback-Vorschau. */
    sourceItemId: z.string().regex(/^[1-9][0-9]{0,19}$/).optional(),
    /** Ungespeicherter Regelentwurf; Auswertung mit `rulesVersion` 0 und `draftHash`. */
    draft: RulesDraft.optional(),
  })
  .strict();
export type MatchRequest = z.infer<typeof MatchRequest>;

export const MatchResponse = z.object({
  items: z.array(ItemView),
  appliedRules: z.array(z.object({ ruleId: z.string(), kind: z.string(), hits: z.number().int().nonnegative() })),
  usedFallback: z.boolean(),
  excludedSelf: z.boolean(),
  /** Zählung der Treffer (Diagnose), nicht die Freigabe der Auswahl. */
  completeness: z.enum(['complete', 'partial', 'unknown']),
  /** Auswahl bis zum Limit vollständig bestimmt (core `MatchResult.selectionComplete`, fehlt = false). */
  selectionComplete: z.boolean(),
  missingData: z.array(z.string()),
  context: z.object({
    site: EbaySiteId,
    rulesVersion: Version,
    dataVersion: z.string(),
    engineVersion: z.string(),
    referenceTime: IsoDateTime,
    seed: z.string(),
    maxItems: z.number().int(),
    /** Nur bei einer Vorschau eines ungespeicherten Entwurfs. */
    draftHash: z.string().optional(),
    /** Site des Ausgangsangebots, nur wenn sie von der Galeriesite abweicht (S7). */
    sourceSite: EbaySiteId.optional(),
  }),
  /**
   * Einbettcode wie im Altsystem (`static_gallery::make_html_code`, Erzeuger aus `packages/gallery`) für
   * diese Auswahl; nur für gespeicherte Regeln und Konten mit öffentlicher Kennung `u`.
   */
  embed: z.object({ html: z.string(), cssUrl: z.string() }).optional(),
});
export type MatchResponse = z.infer<typeof MatchResponse>;

/**
 * `GET …/galleries/:id/preview`: Angebote ohne Regeln wie Legacy ohne Ausgangsangebot (`p=0`) und der Einbettcode dazu;
 * `embed` fehlt ohne öffentliche Kennung `u` oder bei nicht prüfbarer Quelle.
 */
export const GalleryPreviewQuery = z.object({ box: z.enum(['1']).optional() }).strict();
export const GalleryPreviewResponse = z.object({
  items: z.array(ItemView).max(100),
  maxItems: z.number().int(),
  embed: z.object({ html: z.string(), cssUrl: z.string() }).optional(),
});
export type GalleryPreviewResponse = z.infer<typeof GalleryPreviewResponse>;
